import React, { FC, useEffect, useState } from 'react';
import { Selector } from 'common/components/Selector/Selector';
import { Hamburger } from 'common/svg';
import { getStorageStoreCode, removeStorageStoreCode, updateStorageStoreCode } from 'entities/Store/Store.helper';

export const ALL_STORES_VALUE = 'all';

interface IComponentProps {
  options?: { value?: string | number; label: string }[];
  onChange: (value?: string | number) => void;
  allowAll?: boolean;
  allowClear?: boolean;
}

export const StoreSelectorCir: FC<IComponentProps> = ({ options = [], onChange, allowAll = false, allowClear = true }) => {
  const [value, setValue] = useState<string | number | undefined>(undefined);
  const [defaultValue] = useState(() => getStorageStoreCode());

  const effectiveOptions = allowAll
    ? [{ value: ALL_STORES_VALUE, label: 'All stores' }, ...(options)]
    : options;

  useEffect(() => {
    if (!options.length) {
      return;
    }

    // A non-allowAll selector (e.g. Generate PO) can't aggregate — a stored 'all' from
    // another page must not leak here. Treat it as no default and pick the first store.
    const usableDefault = defaultValue && (allowAll || defaultValue !== ALL_STORES_VALUE) ? defaultValue : undefined;

    if (usableDefault) {
      setValue(usableDefault === ALL_STORES_VALUE ? undefined : usableDefault);
      onChange(usableDefault === ALL_STORES_VALUE ? undefined : usableDefault);
    } else {
      const firstValue = options[0].value;

      if (firstValue) {
        updateStorageStoreCode(firstValue.toString());
        setValue(firstValue);
        onChange(firstValue);
      }
    }
  }, [options, defaultValue, allowAll]);

  const clearSelection = () => {
    removeStorageStoreCode();
    setValue(undefined);
    onChange(undefined);
  };

  const handleChange = (value?: string | number) => {
    if (!value) {
      clearSelection();
      return;
    }

    updateStorageStoreCode(value.toString());
    setValue(value);
    onChange(value === ALL_STORES_VALUE ? undefined : value);
  };

  return (
    <Selector
      suffix={<Hamburger />}
      placeholder={
        <>
          <Hamburger />
          <span>All stores</span>
        </>
      }
      searchable
      options={effectiveOptions}
      value={value}
      onChange={handleChange}
      allowClear={allowClear && !allowAll}
    />
  );
};
